import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, btn, btnGhost, label, inputStyle } from "../roads/_roadsCommon";

const STATUS = { OK: "#22c55e", WARN_LOW_VELOCITY: "#f59e0b",
                   WARN_HIGH_VELOCITY: "#f59e0b", FAIL_CAPACITY: "#ef4444" };
const NODE_COLOR = { manhole: "#3aa0ff", "catch-basin": "#22c55e",
                       outfall: "#F26B1F", "pump-station": "#a855f7" };

/** Ch 24.1 — Drainage Network plan-view editor. */
export default function DrainageNetworkPanel({ projectId }) {
  const [palette, setPalette] = useState({ node_types: [] });
  const [networks, setNetworks] = useState([]);
  const [active, setActive] = useState(null);
  const [mode, setMode] = useState("select"); // select | add-node | add-pipe
  const [addType, setAddType] = useState("manhole");
  const [pipeStart, setPipeStart] = useState(null);
  const [intensity, setIntensity] = useState(60);
  const [err, setErr] = useState("");
  const svgRef = useRef(null);

  const reload = async () => {
    try {
      const [p, n] = await Promise.all([
        api.get(`/projects/${projectId}/drainage/palette`),
        api.get(`/projects/${projectId}/drainage/networks`),
      ]);
      setPalette(p.data); setNetworks(n.data.networks || []);
      if (!active && n.data.networks?.[0]) setActive(n.data.networks[0]);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    const name = window.prompt("Network name:", `Net-${networks.length + 1}`);
    if (!name) return;
    try {
      const { data } = await api.post(`/projects/${projectId}/drainage/networks`,
        { name, design_intensity_mm_hr: intensity,
          nodes: [{ id: "MH1", type: "manhole", x: 0, y: 0,
                       ground_level_m: 100, invert_m: 97 },
                    { id: "OUT", type: "outfall", x: 200, y: 0,
                       ground_level_m: 99, invert_m: 96 }],
          pipes: [{ id: "P1", from_node: "MH1", to_node: "OUT",
                       length_m: 200, invert_up_m: 97, invert_dn_m: 96 }],
          subcatchments: [] });
      setActive(data.network); reload();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const persist = async (mutator) => {
    if (!active) return;
    const next = mutator({ ...active, nodes: [...active.nodes],
                              pipes: [...active.pipes],
                              subcatchments: [...(active.subcatchments || [])] });
    try {
      const { data } = await api.put(
        `/projects/${projectId}/drainage/networks/${active.id}`,
        { name: next.name,
          nodes: next.nodes, pipes: next.pipes,
          subcatchments: next.subcatchments,
          design_intensity_mm_hr: next.design_intensity_mm_hr || intensity });
      setActive({ ...next, ...data });
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const onCanvasClick = (e) => {
    if (!active) return;
    if (mode !== "add-node") return;
    const pt = svgPoint(e, svgRef.current, viewBox);
    const id = (addType === "manhole" ? "MH" :
                  addType === "catch-basin" ? "CB" :
                  addType === "outfall" ? "OUT" : "PS")
                  + (active.nodes.length + 1);
    persist((n) => {
      n.nodes.push({ id, type: addType, x: Math.round(pt.x),
                       y: Math.round(pt.y),
                       ground_level_m: 100, invert_m: 97 });
      return n;
    });
    setMode("select");
  };

  const clickNode = (nid) => {
    if (mode !== "add-pipe") return;
    if (!pipeStart) { setPipeStart(nid); return; }
    if (pipeStart === nid) { setPipeStart(null); return; }
    const pid = "P" + (active.pipes.length + 1);
    persist((n) => {
      n.pipes.push({ id: pid, from_node: pipeStart, to_node: nid,
                       invert_up_m: 97, invert_dn_m: 96 });
      return n;
    });
    setPipeStart(null); setMode("select");
  };

  const editNodeField = (idx, k, v) => persist((n) => {
    n.nodes[idx] = { ...n.nodes[idx], [k]: parseFloat(v) || 0 };
    return n;
  });
  const delNode = (idx) => {
    if (!window.confirm("Delete node + connected pipes?")) return;
    persist((n) => {
      const nid = n.nodes[idx].id;
      n.nodes.splice(idx, 1);
      n.pipes = n.pipes.filter((p) => p.from_node !== nid && p.to_node !== nid);
      n.subcatchments = (n.subcatchments || []).filter(
        (s) => s.drains_to_node !== nid);
      return n;
    });
  };

  const addSubcatchment = () => persist((n) => {
    const id = "SC" + ((n.subcatchments?.length || 0) + 1);
    const drains = n.nodes[0]?.id || "";
    n.subcatchments = [...(n.subcatchments || []),
      { id, drains_to_node: drains, area_ha: 1, runoff_C: 0.7 }];
    return n;
  });
  const editSub = (idx, k, v) => persist((n) => {
    n.subcatchments[idx] = { ...n.subcatchments[idx],
      [k]: k === "drains_to_node" ? v : parseFloat(v) || 0 };
    return n;
  });
  const delSub = (idx) => persist((n) => {
    n.subcatchments.splice(idx, 1); return n;
  });

  const autoSize = async () => {
    if (!active) return;
    try {
      const { data } = await api.post(
        `/projects/${projectId}/drainage/networks/${active.id}/auto-size`,
        { design_intensity_mm_hr: intensity });
      const r = await api.get(
        `/projects/${projectId}/drainage/networks/${active.id}`);
      setActive(r.data.network);
      const summary = data.sized_pipes.map((p) =>
        `${p.pipe_id} → Ø${p.selected_diameter_mm}mm · ${p.design_flow_m3_s.toFixed(3)} m³/s · ${p.status}`)
        .join("\n");
      alert(`Auto-sized at i=${data.design_intensity_mm_hr} mm/h:\n${summary}`);
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
  };

  const viewBox = useMemo(() => {
    if (!active?.nodes?.length) return "-50 -50 400 200";
    const xs = active.nodes.map((n) => n.x), ys = active.nodes.map((n) => n.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const w = (maxX - minX) || 200, h = (maxY - minY) || 100;
    const pad = Math.max(w, h) * 0.15 + 30;
    return `${minX - pad} ${-(maxY + pad)} ${w + 2 * pad} ${h + 2 * pad}`;
  }, [active?.nodes]);

  if (!active) {
    return (
      <div data-testid="drainage-network" style={{ padding: 24 }}>
        <div style={label}>Wave H · Ch 24 — Drainage Network</div>
        <p style={{ fontSize: 12, color: "var(--pf-muted)", marginBottom: 16 }}>
          No drainage networks yet.
        </p>
        <button data-testid="drainage-new" style={btn} onClick={create}>
          + New network</button>
      </div>
    );
  }

  return (
    <div data-testid="drainage-network" style={{ display: "grid",
            gridTemplateColumns: "320px 1fr 280px", gap: 8, height: "calc(100vh - 280px)", minHeight: 700 }}>
      <div style={{ ...card, padding: 8, overflowY: "auto" }}>
        <div style={{ display: "flex", gap: 4 }}>
          <select data-testid="drainage-select" style={inputStyle}
            value={active.id} onChange={(e) =>
              setActive(networks.find((n) => n.id === e.target.value) || null)}>
            {networks.map((n) => <option key={n.id} value={n.id}>{n.name}</option>)}
          </select>
          <button style={btnGhost} onClick={create}>+</button>
        </div>

        <div style={{ ...label, marginTop: 10 }}>Add</div>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {(palette.node_types || []).map((nt) => (
            <button key={nt.id} data-testid={`pal-${nt.id}`}
              style={{ ...(addType === nt.id && mode === "add-node" ? btn : btnGhost),
                        padding: "3px 6px", fontSize: 10 }}
              onClick={() => { setAddType(nt.id); setMode("add-node"); }}>
              {nt.label.split(" / ")[0]}
            </button>
          ))}
          <button data-testid="pal-pipe"
            style={mode === "add-pipe" ? btn : btnGhost}
            onClick={() => { setMode(mode === "add-pipe" ? "select" : "add-pipe");
                              setPipeStart(null); }}>
            + Pipe
          </button>
        </div>

        <div style={{ ...label, marginTop: 10 }}>Nodes ({active.nodes.length})</div>
        <table style={{ width: "100%", fontSize: 10 }}>
          <thead><tr style={{ borderBottom: "1px solid var(--pf-border)" }}>
            <th style={{ textAlign: "left", padding: 2 }}>id</th>
            <th style={{ textAlign: "right", padding: 2 }}>GL</th>
            <th style={{ textAlign: "right", padding: 2 }}>IL</th>
            <th></th>
          </tr></thead>
          <tbody>
            {active.nodes.map((n, i) => (
              <tr key={n.id} data-testid={`node-row-${n.id}`}>
                <td style={{ padding: 2 }}>
                  <span style={{ display: "inline-block", width: 6, height: 6,
                                    borderRadius: "50%", background: NODE_COLOR[n.type],
                                    marginRight: 3 }} />
                  {n.id}
                </td>
                <td><input style={{ ...inputStyle, padding: 1, fontSize: 10 }} type="number"
                              value={n.ground_level_m}
                              onBlur={(e) => editNodeField(i, "ground_level_m", e.target.value)}
                              onChange={() => {}} /></td>
                <td><input style={{ ...inputStyle, padding: 1, fontSize: 10 }} type="number"
                              value={n.invert_m}
                              onBlur={(e) => editNodeField(i, "invert_m", e.target.value)}
                              onChange={() => {}} /></td>
                <td><button data-testid={`node-del-${n.id}`}
                              style={{ ...btnGhost, padding: "1px 5px", fontSize: 10 }}
                              onClick={() => delNode(i)}>×</button></td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ ...label, marginTop: 10 }}>
          Subcatchments ({active.subcatchments?.length || 0})</div>
        {(active.subcatchments || []).map((sc, i) => (
          <div key={i} data-testid={`sub-row-${i}`} style={{ display: "flex", gap: 3,
                marginTop: 3, fontSize: 10 }}>
            <select style={{ ...inputStyle, padding: 1, fontSize: 10, width: 60 }}
              value={sc.drains_to_node}
              onChange={(e) => editSub(i, "drains_to_node", e.target.value)}>
              {active.nodes.map((n) => <option key={n.id} value={n.id}>{n.id}</option>)}
            </select>
            <input style={{ ...inputStyle, padding: 1, fontSize: 10, width: 50 }} type="number" step="0.1"
              value={sc.area_ha}
              onChange={(e) => editSub(i, "area_ha", e.target.value)} />
            <input style={{ ...inputStyle, padding: 1, fontSize: 10, width: 50 }} type="number" step="0.05"
              value={sc.runoff_C}
              onChange={(e) => editSub(i, "runoff_C", e.target.value)} />
            <button style={{ ...btnGhost, padding: "1px 5px", fontSize: 10 }}
              onClick={() => delSub(i)}>×</button>
          </div>
        ))}
        <button data-testid="sub-add" style={{ ...btnGhost, width: "100%", marginTop: 4 }}
          onClick={addSubcatchment}>+ Subcatchment</button>

        <div style={{ ...label, marginTop: 12 }}>Auto-size</div>
        <div style={{ display: "flex", gap: 4 }}>
          <input data-testid="drainage-i" style={inputStyle} type="number"
            value={intensity} onChange={(e) =>
              setIntensity(parseFloat(e.target.value) || 60)} />
          <button data-testid="drainage-autosize" style={btn} onClick={autoSize}>
            Run
          </button>
        </div>
        <div style={{ fontSize: 9, color: "#888", marginTop: 2 }}>
          i (mm/h) — Rational method peak rainfall intensity
        </div>
      </div>

      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <div style={{ padding: 6, fontSize: 11, color: "var(--pf-muted)",
                       borderBottom: "1px solid var(--pf-border)" }}>
          {mode === "add-node" ? `Click canvas to drop a ${addType}…`
            : mode === "add-pipe" ? `Click 2 nodes to add a pipe…`
            : `Network: ${active.name} · ${active.nodes.length} nodes · ${active.pipes.length} pipes`}
        </div>
        <svg ref={svgRef} viewBox={viewBox} data-testid="drainage-canvas"
              onClick={onCanvasClick}
              style={{ width: "100%", height: "calc(100% - 32px)", background: "#0a1220",
                       cursor: mode === "add-node" ? "crosshair" : "default" }}>
          {/* Pipes */}
          {active.pipes.map((p, i) => {
            const a = active.nodes.find((n) => n.id === p.from_node);
            const b = active.nodes.find((n) => n.id === p.to_node);
            if (!a || !b) return null;
            const d = p.diameter_mm || 0;
            const status = p.sizing_status;
            const colour = STATUS[status] || "#3aa0ff";
            return (
              <g key={i} data-testid={`pipe-${p.id}`}>
                <line x1={a.x} y1={-a.y} x2={b.x} y2={-b.y}
                        stroke={colour} strokeWidth={Math.max(1, d / 100)} />
                <text x={(a.x + b.x) / 2} y={-(a.y + b.y) / 2 - 3}
                       fontSize={6} fill="#fff" textAnchor="middle"
                       fontFamily="monospace">{p.id} {d ? `Ø${d}` : ""}</text>
              </g>
            );
          })}
          {/* Nodes */}
          {active.nodes.map((n, i) => (
            <g key={n.id} data-testid={`node-${n.id}`}
                onClick={() => clickNode(n.id)}
                style={{ cursor: mode === "add-pipe" ? "pointer" : "default" }}>
              <circle cx={n.x} cy={-n.y}
                        r={n.type === "outfall" ? 7 : 5}
                        fill={NODE_COLOR[n.type] || "#fff"}
                        stroke={pipeStart === n.id ? "#fff" : "#fff"}
                        strokeWidth={pipeStart === n.id ? 2.5 : 1} />
              <text x={n.x} y={-n.y - 10} fontSize={6} fill="#fff" textAnchor="middle"
                     fontFamily="monospace">{n.id}</text>
            </g>
          ))}
        </svg>
      </div>

      <div style={{ ...card, padding: 8, overflowY: "auto" }}>
        <div style={label}>Pipes ({active.pipes.length})</div>
        {active.pipes.map((p) => (
          <div key={p.id} data-testid={`pipe-row-${p.id}`}
            style={{ padding: 4, marginTop: 4, fontSize: 10,
                      background: "rgba(255,255,255,0.04)", borderRadius: 3,
                      borderLeft: `3px solid ${STATUS[p.sizing_status] || "#888"}` }}>
            <div><b>{p.id}</b> · {p.from_node} → {p.to_node}</div>
            <div>L = {(p.length_m || 0).toFixed(1)}m · slope {p.slope ? p.slope.toFixed(4) : "—"}</div>
            {p.diameter_mm && <div>Ø {p.diameter_mm}mm · Q_full = {p.capacity_m3_s} m³/s · V = {p.velocity_full_m_s} m/s</div>}
            {p.design_flow_m3_s !== undefined &&
              <div style={{ color: STATUS[p.sizing_status] || "#888", fontWeight: 600 }}>
                Q_design = {p.design_flow_m3_s} · {p.sizing_status}
              </div>}
          </div>
        ))}
        {err && <div style={{ marginTop: 8, color: "#FF8888", fontSize: 11 }}>{err}</div>}
      </div>
    </div>
  );
}

function svgPoint(evt, svg, viewBox) {
  const rect = svg.getBoundingClientRect();
  const [vx, vy, vw, vh] = (viewBox || "0 0 1000 1000").split(" ").map(Number);
  const xRatio = (evt.clientX - rect.left) / rect.width;
  const yRatio = (evt.clientY - rect.top) / rect.height;
  return { x: vx + xRatio * vw, y: -(vy + yRatio * vh) };
}
